/*
  DORVETTI — Hero apilado: controlador (MOV-012). Sin librerías.

  - Modo CSS: no mueve nada; solo gestiona vídeos, foco y editor.
  - Modo JS (ritmo > 100 %): translateY de cada escena según el progreso del scroll.
  - El scroll nunca se intercepta: un único listener pasivo, trabajo agrupado en rAF y
    geometría cacheada (sin lecturas de layout por fotograma).
  - En escritorio este Horizon desplaza `.page-wrapper`, no la ventana: se detecta el
    contenedor que hace scroll y se vuelve a detectar al cambiar el tamaño.
  - Vídeo: HERO 01 con prioridad; el resto se prepara tras la carga y justo antes de verse.
    Solo se reproducen las escenas visibles (la cubierta, aunque esté «en pantalla», se pausa).
*/

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const instances = new Map();

  function getScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const overflowY = getComputedStyle(wrapper).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && wrapper.scrollHeight > wrapper.clientHeight + 1) {
        return wrapper;
      }
    }
    return window;
  }

  const scrollTopOf = (scroller) => (scroller === window ? window.scrollY : scroller.scrollTop);

  class Stack {
    constructor(root) {
      this.root = root;
      this.scenes = Array.from(root.querySelectorAll('[data-dv-scene]'));
      this.videos = this.scenes.map((scene) => scene.querySelector('video'));
      this.loaded = this.scenes.map(() => '');
      this.jsMode = root.dataset.mode === 'js';
      this.pace = this.jsMode ? parseFloat(getComputedStyle(root).getPropertyValue('--dv-pace')) || 1 : 1;
      this.pinned = true;
      this.height = 0;
      this.top = 0;
      this.bottom = 0;
      this.span = 1;
      this.inView = true;
      this.pageLoaded = document.readyState === 'complete';
      this.pending = false;
      this.scroller = getScroller();

      this.onScroll = (event) => {
        const target = event.target;
        if (target === document || target === this.scroller) this.request();
      };
      this.onResize = () => {
        this.scroller = getScroller();
        this.measure();
        this.request();
      };
      this.onMediaChange = () => {
        this.resetSources();
        this.onResize();
      };
      this.onVisibility = () => this.request();
      this.onLoad = () => {
        this.pageLoaded = true;
        this.measure();
        this.request();
        window.setTimeout(() => {
          this.firstReady = true;
          this.request();
        }, 4000);
      };

      this.focusHandlers = this.scenes.map((scene, index) => {
        const handler = () => {
          if (!this.pinned) return;
          const target = this.top + index * this.span;
          if (Math.abs(scrollTopOf(this.scroller) - target) > 1) {
            this.scroller.scrollTo({ top: target, behavior: 'instant' });
          }
        };
        scene.addEventListener('focusin', handler);
        return handler;
      });

      this.videos.forEach((video) => {
        if (!video) return;
        video.addEventListener('playing', () => video.classList.add('is-playing'));
      });

      // La escena siguiente no empieza a descargarse hasta que el HERO 01 puede reproducirse
      // sin cortes (o, como tarde, unos segundos después de la carga): no compiten por la red.
      this.firstReady = !this.videos[0];
      this.videos[0]?.addEventListener(
        'canplaythrough',
        () => {
          this.firstReady = true;
          this.request();
        },
        { once: true }
      );

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          this.request();
        });
        this.observer.observe(root);
      }

      // Captura en document: recibe tanto el scroll de la ventana como el de `.page-wrapper`.
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      reduceMotion.addEventListener?.('change', this.onMediaChange);
      mobileQuery.addEventListener?.('change', this.onMediaChange);
      if (!this.pageLoaded) window.addEventListener('load', this.onLoad, { once: true });

      this.measure();
      // HERO 01 con prioridad: su vídeo empieza a cargar sin esperar a la carga de la página.
      if (!reduceMotion.matches) this.load(0);
      this.update();
    }

    destroy() {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onLoad);
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduceMotion.removeEventListener?.('change', this.onMediaChange);
      mobileQuery.removeEventListener?.('change', this.onMediaChange);
      this.observer?.disconnect();
      this.scenes.forEach((scene, index) => scene.removeEventListener('focusin', this.focusHandlers[index]));
      this.videos.forEach((video) => video && !video.paused && video.pause());
    }

    measure() {
      this.root.classList.remove('is-unpinned');
      const doesNotFit = this.scenes.some((scene) => {
        const content = scene.querySelector('.dv-scene__content');
        return content && content.offsetHeight > scene.clientHeight;
      });
      if (doesNotFit) this.root.classList.add('is-unpinned');
      this.pinned = !reduceMotion.matches && !doesNotFit;

      const scrollTop = scrollTopOf(this.scroller);
      const rect = this.root.getBoundingClientRect();
      this.height = this.scenes[0] ? this.scenes[0].getBoundingClientRect().height : window.innerHeight;
      this.top = rect.top + scrollTop;
      this.bottom = this.top + rect.height;
      this.span = Math.max(1, this.height * this.pace);
      if (!this.pinned) this.scenes.forEach((scene) => (scene.style.transform = ''));
    }

    /* Cuánto ha subido cada escena: 0 = abajo, fuera · 1 = cubre la pantalla. */
    progress(scrollTop) {
      const y = scrollTop - this.top;
      return this.scenes.map((_, index) => {
        if (index === 0) return 1;
        return Math.min(1, Math.max(0, (y - (index - 1) * this.span) / this.span));
      });
    }

    visible(progress, scrollTop) {
      const viewport = window.innerHeight;
      if (!this.pinned) {
        return this.scenes.map((scene) => {
          const rect = scene.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < viewport;
        });
      }
      // IntersectionObserver da por visible una pila que solo toca el borde (0 px):
      // la decisión final usa la geometría cacheada.
      const onScreen = this.inView && scrollTop < this.bottom && scrollTop + viewport > this.top;
      const last = this.scenes.length - 1;
      return progress.map((value, index) => onScreen && value > 0 && (index === last || progress[index + 1] < 1));
    }

    pickSource(video) {
      return (mobileQuery.matches ? video.dataset.srcMobile : video.dataset.srcDesktop) || video.dataset.srcDesktop || '';
    }

    load(index) {
      const video = this.videos[index];
      if (!video) return;
      const source = this.pickSource(video);
      if (!source || this.loaded[index] === source) return;
      this.loaded[index] = source;
      video.preload = 'auto';
      video.src = source;
    }

    play(index) {
      const video = this.videos[index];
      if (!video) return;
      this.load(index);
      if (!video.paused) return;
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }

    pause(index) {
      const video = this.videos[index];
      if (video && !video.paused) video.pause();
    }

    resetSources() {
      this.videos.forEach((video, index) => {
        if (!video || !this.loaded[index]) return;
        if (this.pickSource(video) !== this.loaded[index]) {
          video.classList.remove('is-playing');
          this.loaded[index] = '';
          video.removeAttribute('src');
          video.load();
        }
      });
    }

    request() {
      if (this.pending) return;
      this.pending = true;
      requestAnimationFrame(() => this.update());
    }

    update() {
      this.pending = false;
      const scrollTop = scrollTopOf(this.scroller);
      const progress = this.progress(scrollTop);

      if (this.jsMode && this.pinned) {
        this.scenes.forEach((scene, index) => {
          if (index > 0) scene.style.transform = `translate3d(0, ${((1 - progress[index]) * 100).toFixed(3)}%, 0)`;
        });
      }

      const visible = this.visible(progress, scrollTop);
      this.scenes.forEach((_, index) => {
        if (reduceMotion.matches || document.hidden || !visible[index]) {
          this.pause(index);
          return;
        }
        this.play(index);
        // La siguiente se prepara mientras esta está a la vista (tras la carga de la página).
        if (this.pageLoaded && (index > 0 || this.firstReady) && this.videos[index + 1]) this.load(index + 1);
      });
    }

    scrollToScene(index) {
      this.measure();
      const target = this.pinned ? this.top + index * this.span : this.scenes[index].getBoundingClientRect().top + scrollTopOf(this.scroller);
      this.scroller.scrollTo({ top: target, behavior: 'instant' });
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-dv-stack]').forEach((root) => {
      if (!instances.has(root)) instances.set(root, new Stack(root));
    });
  }

  function destroyWithin(scope) {
    instances.forEach((stack, root) => {
      if (scope.contains(root)) {
        stack.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // Editor de temas.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const scene = event.target.closest?.('[data-dv-scene]');
    if (!scene) return;
    const root = scene.closest('[data-dv-stack]');
    const stack = instances.get(root);
    if (stack) stack.scrollToScene(Number(scene.dataset.index) || 0);
  });
})();
